import { Component, Input, Output, EventEmitter, OnChanges, inject } from "@angular/core";
import { KeyValuePipe, NgStyle } from "@angular/common";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { SharedDataService } from "@app/shared/services/shared-data.service";
import { MatrixModel, MatrixCell } from "@models/generic/MatrixModel";
import { MatrixSelectionModel } from "@models/generic/MatrixSelectionModel";
import { firstValueFrom } from "rxjs";

@Component({
    selector: "cerrix-riskmatrix",
    templateUrl: "./cerrix-riskmatrix.component.html",
    styleUrls: ["./cerrix-riskmatrix.component.scss"],
    imports: [NgStyle, KeyValuePipe, TranslatePipe],
    viewProviders: [
        provideTranslationScope(scope),
    ],
})
export class CerrixRiskMatrixComponent implements OnChanges {
    private _sharedDS = inject(SharedDataService);

    @Input() OrganizationID: number;
    @Input() matrix: MatrixModel;

    @Input() readonly: boolean;

    @Input() cellSize = 75;
    @Input() cellSpacing = 2;
    @Input() cellBorder = 0;
    @Input() highlightSelection = true;

    @Input() selected: MatrixCell;
    @Output() selectedChange = new EventEmitter<MatrixCell>();

    lastLoadedOrganization: number = null;

    borderTopCells: Record<string, boolean> = null;
    borderRightCells: Record<string, boolean> = null;

    async ngOnChanges() {
        if (this.OrganizationID > 0 && this.OrganizationID !== this.lastLoadedOrganization) {
            await this.loadFromOrganization();
        }
        if (this.matrix && this.matrix.appetiteRange) {
            this.setSuggestedSelectionOutline(this.matrix.appetiteRange);
        }
    }

    async loadFromOrganization() {
        // Set before the await, so another change of an input during the load does not start a
        // second load of the same organization.
        const organizationID = this.OrganizationID;
        const previousOrganization = this.lastLoadedOrganization;
        this.lastLoadedOrganization = organizationID;
        try {
            const matrix = await firstValueFrom(this._sharedDS.getOrganizationMatrix(organizationID));
            // A response that arrives after another organization took over is stale.
            if (this.lastLoadedOrganization === organizationID) {
                this.matrix = matrix;
            }
        } catch (error) {
            // Let the next change retry. Leave the value alone when another organization took over.
            if (this.lastLoadedOrganization === organizationID) {
                this.lastLoadedOrganization = previousOrganization;
            }
            throw error;
        }
    }

    matrixCellClick(y: number, x: number, color: string) {
        if (this.readonly) {
            return;
        }

        this.selected = <MatrixCell>{
            Likelihood: +y,
            Impact: +x,
            Color: color,
        };

        this.selectedChange.emit(this.selected);
    }

    // Creates an outline for a given range of MatrixSelectionModels
    public setSuggestedSelectionOutline(selectionRange: MatrixSelectionModel[]) {
        if (!selectionRange) {
            this.borderTopCells = null;
            this.borderRightCells = null;
            return;
        }

        this.borderTopCells = {};
        this.borderRightCells = {};

        // Get grouped object with Likelihood as keys
        const selectionRangePerRow = selectionRange.groupBy<MatrixSelectionModel>("Likelihood");

        const likelihoods = Object.keys(selectionRangePerRow).map((x) => +x);
        const maxLikelihood = Math.max(...likelihoods);

        let previousRowMaxImpact: number;
        likelihoods.sortDesc().forEach((likelihood) => {
            const currentRow = selectionRangePerRow[likelihood] as MatrixSelectionModel[];
            let maxImpact = 0;

            currentRow.forEach((col, colIndex) => {
                maxImpact = col.Impact > maxImpact ? col.Impact : maxImpact;

                const colSelector = `${col.Likelihood}-${col.Impact}`;

                //Always give the max likelihood cell a top border and when current rows impact is lower than the previous rows impact
                if (col.Likelihood === maxLikelihood || col.Impact > previousRowMaxImpact) {
                    this.borderTopCells[colSelector] = true;
                }

                // Give the last cell in the row a right border
                if (colIndex === currentRow.length - 1) {
                    this.borderRightCells[colSelector] = true;
                }
            });

            previousRowMaxImpact = maxImpact;
        });
    }
}
